import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { tarefaService } from "@/services/api/tarefa.service";
import { TarefaMensagemDTO } from "@/types/tarefa.type";
import { MensagemTipoAutor } from "@workflow/types";
import { useAuth } from "@/lib/auth-context";

export type MensagemRender = TarefaMensagemDTO & { isOptimistic?: boolean };

export function useTarefaChat(tarefaId: number, tipoAutor: MensagemTipoAutor) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const queryKey = ["tarefa-mensagens", tarefaId];

  // Timestamp da visualização ANTERIOR a esta abertura do chat — usado só pra
  // destacar quais mensagens são "novas" nesta visita (ver TarefaChatMessage).
  // Depois que a marcação acontece, esse valor fica congelado até a tela reabrir.
  const [visualizadoAntes, setVisualizadoAntes] = useState<Date | null>(null);

  const { data: mensagens = [], isLoading } = useQuery<MensagemRender[]>({
    queryKey,
    queryFn: () => tarefaService.getMensagensByTarefaId(tarefaId),
    enabled: !!tarefaId,
    staleTime: 1000 * 30,
  });

  const sendMessageMutation = useMutation({
    mutationFn: (msgTexto: string) => tarefaService.adicionarMensagem(tarefaId, msgTexto),
    onMutate: async (msgTexto) => {
      await queryClient.cancelQueries({ queryKey });
      const previousMessages = queryClient.getQueryData<MensagemRender[]>(queryKey);

      const tempId = Date.now() + Math.random();
      const optimisticMsg: MensagemRender = {
        id: tempId,
        mensagem: msgTexto,
        tipo: tipoAutor,
        criadoEm: new Date(),
        criadoPor: { id: user?.id || 0, nome: user?.nome || "Você" } as any,
        isOptimistic: true,
      };

      queryClient.setQueryData<MensagemRender[]>(queryKey, (old) => [
        ...(old || []),
        optimisticMsg,
      ]);

      return { previousMessages, tempId };
    },
    onSuccess: (realMsg, _variables, context) => {
      queryClient.setQueryData<MensagemRender[]>(queryKey, (old) =>
        old?.map((msg) => (msg.id === context?.tempId ? realMsg : msg)),
      );
    },
    onError: (err, _variables, context) => {
      if (context?.previousMessages) {
        queryClient.setQueryData(queryKey, context.previousMessages);
      }
      toast.error("Erro ao enviar mensagem. Tente novamente.");
      console.error(err);
    },
  });

  // Abrir o chat já marca tudo que existe até agora como visualizado — uma vez,
  // não por mensagem (evita fadiga). Invalida o badge do sino pra não esperar o
  // próximo polling de 60s.
  useEffect(() => {
    if (!tarefaId) return;

    tarefaService
      .marcarVisualizada(tarefaId)
      .then(({ visualizadoAnteriorEm }) => {
        setVisualizadoAntes(visualizadoAnteriorEm ? new Date(visualizadoAnteriorEm) : null);
        queryClient.invalidateQueries({ queryKey: ["tarefas-pendentes-count"] });
        queryClient.invalidateQueries({ queryKey: ["tarefas-notificacoes"] });
      })
      .catch((err) => console.error("Falha ao marcar tarefa como visualizada", err));
  }, [tarefaId, queryClient]);

  /** Mensagem de outra pessoa chegou depois da última vez que eu vi esta tarefa. */
  const isMensagemNova = (msg: MensagemRender) =>
    !!visualizadoAntes &&
    !!msg.criadoEm &&
    Number(msg.criadoPor?.id) !== Number(user?.id) &&
    new Date(msg.criadoEm) > visualizadoAntes;

  return {
    mensagens,
    isLoading,
    sendMessage: (texto: string) => sendMessageMutation.mutate(texto),
    isSending: sendMessageMutation.isPending,
    isMensagemNova,
  };
}
